// @flow

import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import dispatchReportPin from 'app/common/react/components/undiscovery/FlagPin/SimplifiedReportPin/action/dispatchReportPin';
import getImageUrl from 'app/common/react/components/PinRep/utils/getImageUrl';
import i18n from 'app/common/lib/i18n';
import IconButton from 'app/common/react/components/common/ExperimentalLegoGestalt/IconButton';
import PinFeedbackDropdown from 'app/common/react/components/PinRep/components/PinFeedbackDropdown';
import smartTruncate from 'app/common/lib/util/smartTruncate';
import useTrackingCallback from 'app/common/react/components/PinRep/Hooks/useTrackingCallback';
import { getPinGifOrImageSrc } from 'app/common/react/components/growth/unauth/pin/pinUtils';
import { hidePinFeedbackOverlay } from 'app/common/redux/actions/pin/feedback';
import { isVideoPin } from 'app/common/lib/util/PinUtils';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';
import {
  type FeedbackData,
  type FeedbackType,
} from 'app/common/react/components/PinRep/feedback-types';
import { Box, Flyout, Layer } from 'gestalt';
import { type DispatchFunctionType, type State as ReduxState } from 'app/common/redux/types';

const STRING_MAX_LENGTH = 50;
const STRING_MAX_LENGTH_PROMOTED = 60;

function getTruncatedStringFunc(maxLength: number) {
  return function getTruncatedString(str: string = '') {
    return smartTruncate(str.trim(), maxLength);
  };
}

type ReduxStateProps = {|
  isHomefeedContextualMenuTooltipExperience: boolean,
|};

type ReduxDispatchProps = {|
  hidePinFeedbackOverlay: () => void,
|};

type OwnProps = {|
  pin: Pin,

  feedbackData: FeedbackData,

  // Indicates why a pin appears in a given grid
  feedbackType: FeedbackType,

  // The title for the pin (uses more args than just the pin, so determined higher up)
  pinTitle: string,

  // Used to force a hover experience. See PinRepContainer for more context.
  setForcePrimary: (boolean) => void,

  // Should the button use a transparent background? (Used when displayed on a white background)
  transparentBackground?: boolean,
|};

export type Props = {|
  ...ReduxStateProps,
  ...ReduxDispatchProps,
  ...OwnProps,
|};

export function OverflowMenu({
  feedbackData,
  feedbackType,
  hidePinFeedbackOverlay,
  isHomefeedContextualMenuTooltipExperience,
  pin,
  pinTitle,
  setForcePrimary,
  transparentBackground,
}: Props) {
  const { slotIndex, viewParameter, viewType } = useTrackingParameters();

  const [showPinFeedbackModal, setShowPinFeedbackModal] = useState(false);
  const [anchorElement, setAnchorElement] = useState<?HTMLDivElement>();

  const {
    ad_match_reason: adMatchReason,
    board,
    category: pinCategory,
    description,
    id: pinId = '',
    images,
    is_promoted: isPromoted,
    pinner,
    promoter,
    recommendation_reason: recommendationReason,
    source_interest: sourceInterest,
    videos,
  } = pin;

  const getTruncatedString = getTruncatedStringFunc(
    isPromoted ? STRING_MAX_LENGTH_PROMOTED : STRING_MAX_LENGTH,
  );
  const pinDescription = getTruncatedString(description);

  const imageUrl = getImageUrl(images, '236x');
  const isVideo = isVideoPin(videos);

  const handleButtonClickLogging = useTrackingCallback(PCONST.thrift.EventType.CLICK, {
    // TODO: Should component really be hard-coded here?
    component: PCONST.thrift.ComponentType.FLOWED_PIN,
    element: PCONST.thrift.ElementType.PIN_FEEDBACK_DIALOG_BUTTON,
    feedback_type: feedbackType,
    objectId: pinId,
  });

  const dismissFlyout = () => {
    setForcePrimary(false);
    hidePinFeedbackOverlay();
    setShowPinFeedbackModal(false);
  };

  const handleFeedbackButtonClick = ({ event }: { event: SyntheticMouseEvent<> }) => {
    if (showPinFeedbackModal) {
      dismissFlyout();
    } else {
      setForcePrimary(true);
      setShowPinFeedbackModal(true);
    }
    handleButtonClickLogging();
  };

  const handleLinkClick = () => {
    if (showPinFeedbackModal) {
      dismissFlyout();
    }
  };

  const isHomefeedContextualMenuTooltip =
    slotIndex === 1 && isHomefeedContextualMenuTooltipExperience;

  return (
    <Fragment>
      <Box data-test-id="feedback-button" ref={setAnchorElement}>
        <IconButton
          accessibilityLabel={i18n._(
            'More information',
            'accessibility label for overflow menu button',
          )}
          bgColor={transparentBackground ? 'transparent' : 'white'}
          icon="ellipsis"
          iconColor="darkGray"
          iconSize={12}
          onClick={handleFeedbackButtonClick}
          size="sm"
        />
      </Box>
      {showPinFeedbackModal && (
        <Layer>
          <Flyout
            anchor={anchorElement}
            idealDirection="right"
            onDismiss={dismissFlyout}
            positionRelativeToAnchor={false}
            size="md"
          >
            <PinFeedbackDropdown
              ad_match_reason={adMatchReason}
              bestPinImgSrc={getPinGifOrImageSrc(pin)}
              board={board}
              feedbackData={feedbackData}
              feedbackType={feedbackType}
              hideDownload={isVideo}
              isHomefeedContextualMenuTooltip={isHomefeedContextualMenuTooltip}
              onLinkClick={handleLinkClick}
              onHide={dismissFlyout}
              pinCategory={pinCategory}
              pinDescription={pinDescription}
              pinId={pinId}
              pinImgSrc={imageUrl}
              pinner={pinner}
              pinTitle={pinTitle}
              promoter={promoter}
              recommendationReason={recommendationReason}
              reportPin={
                // $FlowFixMe Pin types don't match
                dispatchReportPin({
                  pin,
                  viewParameter,
                  viewType,
                })
              }
              sourceInterest={sourceInterest}
              viewParameter={viewParameter}
              viewType={viewType}
            />
          </Flyout>
        </Layer>
      )}
    </Fragment>
  );
}

function mapStateToProps({ experiences }: ReduxState): ReduxStateProps {
  return {
    isHomefeedContextualMenuTooltipExperience:
      experiences &&
      experiences.eligibleExperiences &&
      experiences.eligibleExperiences[PCONST.thrift.Placement.HOMEFEED_HIDE_PIN] &&
      experiences.eligibleExperiences[PCONST.thrift.Placement.HOMEFEED_HIDE_PIN].experience_id ===
        PCONST.thrift.Experience.WEB_HOMEFEED_CONTEXTUAL_MENU_TOOLTIP,
  };
}

function mapDispatchToProps(dispatch: DispatchFunctionType, { pin }: OwnProps): ReduxDispatchProps {
  return {
    hidePinFeedbackOverlay: () => {
      dispatch(hidePinFeedbackOverlay(pin.id));
    },
  };
}

export default connect<
  Props,
  OwnProps,
  ReduxStateProps,
  ReduxDispatchProps,
  ReduxState,
  DispatchFunctionType,
>(
  mapStateToProps,
  mapDispatchToProps,
)(OverflowMenu);
